import "@/App.css";
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { AuthProvider, useAuth } from "./lib/AuthContext";
import { SubscriptionProvider } from "./lib/SubscriptionContext";
import { WatchStatusProvider } from "./lib/WatchStatusContext";
import Navbar from "./components/Navbar";
import InstallPrompt from "./components/InstallPrompt";
import ScrollToTop from "./components/ScrollToTop";
import FloatingFilmi from "./components/FloatingFilmi";
import ApiHealthPill from "./components/ApiHealthPill";
import PaywallModal from "./components/PaywallModal";
import SignInNudgeModal from "./components/SignInNudgeModal";
// FirstVisitTrialPrompt suppressed while SignInNudgeModal runs the conversion flow.
// Re-enable by restoring: import FirstVisitTrialPrompt from "./components/FirstVisitTrialPrompt";
import SubtleTrialRibbon from "./components/SubtleTrialRibbon";
import SaveToListModal from "./components/SaveToListModal";
import Home from "./pages/Home";
const Discover = lazy(() => import("./pages/Discover"));
const Concierge = lazy(() => import("./pages/Concierge"));
const MovieDetails = lazy(() => import("./pages/MovieDetails"));
const Watchlist = lazy(() => import("./pages/Watchlist"));
const Profile = lazy(() => import("./pages/Profile"));
const Insights = lazy(() => import("./pages/Insights"));
const AuthGoogle = lazy(() => import("./pages/AuthGoogle"));
const CollectionPage = lazy(() => import("./pages/CollectionPage"));
const SearchPage = lazy(() => import("./pages/SearchPage"));
const SurpriseMe = lazy(() => import("./pages/SurpriseMe"));
const TopList = lazy(() => import("./pages/TopList"));
const ListPage = lazy(() => import("./pages/ListPage"));
const Support = lazy(() => import("./pages/Support"));
const Creators = lazy(() => import("./pages/Creators"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const ShippingPolicy = lazy(() => import("./pages/ShippingPolicy"));
const CancellationRefunds = lazy(() => import("./pages/CancellationRefunds"));
const Admin = lazy(() => import("./pages/Admin"));
const PreviewRail = lazy(() => import("./pages/PreviewRail"));
const SimilarPage = lazy(() => import("./pages/SimilarPage"));
const Social = lazy(() => import("./pages/Social"));
const PlatformPage = lazy(() => import("./pages/PlatformPage"));
const WhereToWatch = lazy(() => import("./pages/WhereToWatch"));
const WatchlistShare = lazy(() => import("./pages/WatchlistShare"));
const CategoryPage = lazy(() => import("./pages/CategoryPage"));

// Redirects old /movie/:id and /tv/:id short URLs to canonical /title/:mediaType/:id
function LegacyTitleRedirect({ mediaType }) {
  const { id } = useParams();
  return <Navigate to={`/title/${mediaType}/${id}`} replace />;
}

// SpamParamGuard — meta-tag fallback for production where setupProxy.js
// doesn't run. The X-Robots-Tag HTTP header is the preferred signal; this
// <meta> tag is a belt-and-suspenders fallback for crawlers that support JS.
// Neither approach blanks the page — the app renders normally.
function SpamParamGuard() {
  const { search } = useLocation();
  const isSpam = /[?&]f=/.test(search);
  if (!isSpam) return null;
  return (
    <Helmet>
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
  );
}

function AppShell() {
  const location = useLocation();

  // GA4 SPA page-view tracking
  useEffect(() => {
    const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
    if (!GA_ID || typeof window.gtag !== "function") return;
    window.gtag("config", GA_ID, {
      page_path: location.pathname + location.search,
      page_title: document.title,
    });
  }, [location.pathname, location.search]);

  // Native page-view tracking (for in-app admin analytics)
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    if (!API) return;
    // Use a stable anonymous session id stored in sessionStorage
    let sid = sessionStorage.getItem("_fm_sid");
    if (!sid) {
      sid = `anon_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
      sessionStorage.setItem("_fm_sid", sid);
    }
    const page = location.pathname;
    const referrer = document.referrer ? new URL(document.referrer).pathname : "";
    // Fire-and-forget, non-blocking
    fetch(`${API}/api/analytics/pv`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ page, referrer, session_id: sid }),
      keepalive: true,
    }).catch(() => {});
  }, [location.pathname]);

  return (
    <>
      <SpamParamGuard />
      <Navbar />
      <ScrollToTop />
      <Suspense fallback={<div>Loading...</div>}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/discover" element={<Discover />} />
          <Route path="/concierge" element={<Concierge />} />
          <Route path="/title/:mediaType/:titleSlug" element={<MovieDetails />} />
          {/* Legacy short URLs → canonical format */}
          <Route path="/movie/:id" element={<LegacyTitleRedirect mediaType="movie" />} />
          <Route path="/tv/:id" element={<LegacyTitleRedirect mediaType="tv" />} />
          <Route path="/collection/:slug" element={<CollectionPage />} />
          <Route path="/similar/:slug" element={<SimilarPage />} />
          <Route path="/where-to-watch/:slug" element={<WhereToWatch />} />
          <Route path="/social" element={<Social />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/surprise-me" element={<SurpriseMe />} />
          <Route path="/top/:mediaType/:scope" element={<TopList />} />
          <Route path="/list/:slug" element={<ListPage />} />
          <Route path="/watchlist" element={<Watchlist />} />
          <Route path="/watchlist/share/:listId" element={<WatchlistShare />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/support" element={<Support />} />
          <Route path="/creators" element={<Creators />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/shipping-policy" element={<ShippingPolicy />} />
          <Route path="/cancellation-refunds" element={<CancellationRefunds />} />
          <Route path="/auth/google" element={<AuthGoogle />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/preview/rail/:key" element={<PreviewRail />} />
          <Route path="/platform/:key" element={<PlatformPage />} />
          {/* Category browse pages */}
          <Route path="/genre/:slug"    element={<CategoryPage />} />
          <Route path="/language/:slug" element={<CategoryPage />} />
          <Route path="/year/:year"     element={<CategoryPage />} />
          {/* /ott/:key — friendly alias for /platform/:key */}
          <Route path="/ott/:key" element={<OttRedirect />} />
        </Routes>
      </Suspense>
    </>
  );
}

function OttRedirect() {
  const { key } = useParams();
  return <Navigate to={`/platform/${key}`} replace />;
}

function WatchStatusInner() {
  const { user } = useAuth();
  return (
    <WatchStatusProvider user={user}>
      <SaveToListModal />
      <AppShell />
    </WatchStatusProvider>
  );
}

function App() {
  // Register service worker for PWA / offline support
  useEffect(() => {
    if ("serviceWorker" in navigator && process.env.NODE_ENV === "production") {
      window.addEventListener("load", () => {
        navigator.serviceWorker
          .register("/service-worker.js")
          .catch(() => { /* silent — fallback to network-only */ });
      });
    }
  }, []);

  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <SubscriptionProvider>
              <WatchStatusInner />
              <InstallPrompt />
              <FloatingFilmi />
              <ApiHealthPill />
              <PaywallModal />
              <SignInNudgeModal />
              {/* <FirstVisitTrialPrompt /> — suppressed while SignInNudgeModal handles free-signup conversion */}
              <SubtleTrialRibbon />
              <Toaster theme="dark" position="bottom-right" />
            </SubscriptionProvider>
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
